import { Dispatch, SetStateAction, memo } from 'react';
import { IOrder, IOrderPaymentLines } from '../../interfaces/Order';
import { ActionIcon, Grid, Menu, rem } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../Table';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import {
  IconCircleDashedCheck,
  IconEdit,
  IconMenu2,
  IconShirt,
  IconTrash
} from '@tabler/icons-react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import {
  buildOrderFolio,
  getOrderCardColorsByAmountDueAndStatus,
  normalizeOrderScheduleTimeValue
} from '../../utils/ordersUtils';
import { IStore } from '../../interfaces/Store';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const ActionMenu = ({
  order,
  searchParamStoreId,
  onOrderAccepted,
  onOrderCancelled,
  showOrderProductsModal
}) => {
  const { t } = useTranslation(['OrdersPage', 'FieldName']);
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const paymentLinesTotal = order.paymentLines?.reduce((prev, next) => {
    return prev + (next?.amountPaid || 0);
  }, 0);

  const rowActions = [
    <Menu.Item
      onClick={() => showOrderProductsModal(order)}
      leftSection={<IconShirt style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:productsModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      disabled={!canEditOrders}
      onClick={() =>
        history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
          storeID: searchParamStoreId
        })
      }
      leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}>
      {t('CommonButtons:edit')}
    </Menu.Item>,

    <Menu.Item
      onClick={() =>
        ConfirmationModal({
          title: t('Notifications:CRUD.acceptedConfirmation'),
          onConfirm: () =>
            onOrderAccepted({
              sequence: order.sequence || '',
              storeId: order?.createdIn?._id || ''
            }),
          onCancel: () => {}
        })
      }
      leftSection={<IconCircleDashedCheck style={{ width: rem(14), height: rem(14) }} />}>
      Aceptar Pedido
    </Menu.Item>,
    <Menu.Item
      onClick={() =>
        ConfirmationModal({
          title: t('Notifications:CRUD.cancelConfirmation'),
          onConfirm: () =>
            onOrderCancelled({
              sequence: order.sequence || '',
              storeId: order.createdIn?._id || ''
            }),
          onCancel: () => {}
        })
      }
      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}>
      Cancelar Pedido
    </Menu.Item>
  ];

  rowActions.push();

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="transparent">
          <IconMenu2 style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>{...rowActions}</Menu.Dropdown>
    </Menu>
  );
};

export default memo(function CleaningOrderTable({
  orders = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  searchParamStoreId,
  onOrderAccepted,
  onOrderCancelled,
  onSendEmailReceipt,
  onPrintTicket,
  showOrderProductsModal
}: {
  orders: any;
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  searchParamStoreId: string;
  onOrderAccepted: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  onDownloadPNGTicket: (order: IOrder) => Promise<void>;
  showOrderProductsModal: (order: IOrder) => void;
}) {
  const { t } = useTranslation(['FieldName']);
  const columns = [
    {
      header: t('FieldName:actions'),
      size: 50, //small column
      accessorFn: (row) => {
        return (
          <ActionMenu
            order={row}
            searchParamStoreId={searchParamStoreId}
            onOrderAccepted={onOrderAccepted}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            showOrderProductsModal={showOrderProductsModal}
          />
        );
      }
    },
    {
      accessorFn: (row) => buildOrderFolio(row?.folioPrefix, row?.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row) => {
        const orderProducts = row?.products || [];
        const orderBuyAndGerProducts = row?.buyAndGetProducts || [];
        const allPieces = getAllPieces(orderProducts, orderBuyAndGerProducts);
        return allPieces;
      },
      header: t('FieldName:pieces')
    },
    {
      accessorFn: (row) => getCustomerFullName(row.customer.name, row.customer.lastName),
      header: t('FieldName:customer')
    },
    {
      accessorFn: (row) => row.customer.phone,
      header: t('FieldName:phone')
    },
    {
      accessorFn: (row) => row.address?.formattedAddress,
      header: t('FieldName:address')
    },
    {
      accessorFn: (row) => formatMoneyString((row.total || 0).toFixed(2)),
      header: t('FieldName:total')
    },
    {
      accessorFn: (row) => {
        if (!row.pickup || !row.pickupInfo?.date) return '';

        const pickupDate = moment(row.pickupInfo.date).format('DD/MM/YYYY');
        const pickupFromTime = row.pickupInfo?.fromTime
          ? normalizeOrderScheduleTimeValue(row.pickupInfo.fromTime)
          : '';
        const pickupToTime = row.pickupInfo?.toTime
          ? normalizeOrderScheduleTimeValue(row.pickupInfo.toTime)
          : '';

        return [pickupDate, [pickupFromTime, pickupToTime].filter(Boolean).join(' - ')]
          .filter(Boolean)
          .join(' ');
      },
      header: t('FieldName:pickupDate')
    },
    {
      accessorFn: (row) => moment(row.deliveryInfo.date).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:deliveryDate')
    },
    {
      accessorFn: (row) => row.notes,
      header: t('FieldName:notes')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={orders}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              mantineTableBodyRowProps: ({ row }: { row: any }) => {
                const paymentLinesTotal = row.original?.paymentLines?.reduce(
                  (prev: number, next: IOrderPaymentLines) => {
                    return prev + (next?.amountPaid || 0);
                  },
                  0
                );
                const amountDue = row.original?.total - paymentLinesTotal;
                const customColor = getOrderCardColorsByAmountDueAndStatus({
                  amountDue,
                  highlightOrderCardsByPaymentPending: (row.original?.createdIn as Partial<IStore>)
                    ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                  status: row.original?.status
                });

                return {
                  style: {
                    backgroundColor: customColor?.headerBackgroundColor,
                    color: customColor?.headerTextColor
                  }
                };
              }
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
